<script lang="ts">
	import { Image } from "@unpic/svelte";
	import { Badge, Heading, Progressbar } from "flowbite-svelte-v1";
	import { cubicInOut } from "svelte/easing";
	import { fade } from "svelte/transition";
	import { twJoin, twMerge } from "tailwind-merge";
	import type { PollAnswer, PollAnswerStats } from "#gen/forms/v1/poll_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import { DEFAULT_ANSWER_LABEL } from "#lib/components/page/components/poll/constants.js";
	import { transformURL } from "#lib/image.js";
	import { formatPercent } from "#lib/number.js";

	interface Props {
		option: PollAnswer;
		stats: PollAnswerStats | undefined;
		selected: boolean;
		readOnly?: boolean;
		answerLabel?: string;
		onClick?: (value: string) => void;
	}

	const { option, stats, selected, readOnly, answerLabel = DEFAULT_ANSWER_LABEL, onClick }: Props = $props();

	const backgroundColor = $derived(!stats ? "bg-(--poll-card-background-color)" : selected ? "bg-(--poll-user-voted-background-color)" : "bg-(--poll-results-background-color)");
	const borderColor = $derived(!stats ? "border-(--poll-card-border-color)" : selected ? "border-(--poll-user-voted-border-color)" : "border-(--poll-results-border-color)");
	const primaryTextColor = $derived(!stats ? "text-(--poll-card-primary-text-color)" : selected ? "text-(--poll-user-voted-primary-text-color)" : "text-(--poll-results-primary-text-color)");
	const secondaryTextColor = $derived(!stats ? "text-(--poll-card-secondary-text-color)" : selected ? "text-(--poll-user-voted-secondary-text-color)" : "text-(--poll-results-secondary-text-color)");

	let imageError = $state(false);

	$effect(() => {
		if (option.image?.imageUrl) {
			imageError = false;
		}
	});

	function handleOnClick() {
		if (!selected) {
			onClick?.(option.value);
		}
	}
</script>

<button type="button" class={twJoin("relative flex h-28 w-full flex-col gap-1 rounded-xl border p-4 transition-colors duration-300 ease-out", backgroundColor, borderColor)} class:pointer-events-none={readOnly} onclick={handleOnClick}>
	<div class="flex h-full w-full items-start gap-4">
		<div class="flex grow items-center gap-2">
			{#if option.image?.imageUrl}
				{#if !imageError}
					<div class={twMerge("h-18 w-18 flex-none rounded-md object-contain transition-all duration-300 ease-out", stats && "h-14 w-14")}>
						{#key option.image?.imageUrl}
							<Image layout="constrained" width={72} height={72} priority={true} src={transformURL(option.image?.imageUrl)} alt={option.image?.altText} onerror={() => (imageError = true)} />
						{/key}
					</div>
				{:else}
					<div class={twMerge("flex h-18 w-18 flex-none items-center justify-center rounded-md border bg-gray-50 object-contain text-gray-400 transition-all duration-300 ease-out", stats && "h-14 w-14")}>
						<Icon iconSet="heroicons" name="photo" />
					</div>
				{/if}
			{/if}
			<div class="flex grow flex-col">
				<Heading tag="h3" class={twJoin("text-left text-base font-semibold", primaryTextColor, option.secondaryLabel ? "line-clamp-1" : "line-clamp-2")}>{option.primaryLabel}</Heading>
				{#if option.secondaryLabel}
					<P size="sm" class={secondaryTextColor}>{option.secondaryLabel}</P>
				{/if}
			</div>
		</div>
		{#if selected}
			<div transition:fade={{ duration: 300, easing: cubicInOut }} class="flex-none py-0.5">
				<Badge color="primary" class="bg-(--poll-your-vote-background-color) font-(--poll-your-vote-font-weight) text-(--poll-your-vote-text-color)" style="text-size: var(--text-xs); text-align: var(--poll-your-vote-text-alignment);">{answerLabel}</Badge>
			</div>
		{/if}
	</div>
	{#if stats}
		<div class="flex w-full items-center gap-2">
			<Progressbar animate={true} tweenDuration={500} progress={stats?.percentage ?? 0} class={selected ? "bg-(--poll-user-voted-percentage-disabled-color)" : "bg-(--poll-results-percentage-disabled-color)"} classes={{ label: selected ? "bg-(--poll-user-voted-percentage-enabled-color)" : "bg-(--poll-results-percentage-enabled-color)" }} />
			<div in:fade={{ duration: 300 }}>
				<P size="sm" weight="semibold" class={selected ? "text-(--poll-user-voted-primary-text-color)" : "text-(--poll-results-primary-text-color)"}>{formatPercent(stats?.percentage ?? 0)}</P>
			</div>
		</div>
	{/if}
</button>
